import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import styles from "./dataTableV2.module.scss";

const getSortIcon = (direction) => {
  switch (direction) {
    case "asc":
      return <i className="fa fa-sort-asc" aria-hidden="true"></i>;

    case "desc":
      return <i className="fa fa-sort-desc" aria-hidden="true"></i>;

    default:
      return <i className="fa fa-sort" aria-hidden="true"></i>;
  }
};

const SortIcon = ({ direction }) => (
  <span className={`${styles.sort_icon}`} aria-hidden="true">
    {getSortIcon(direction)}
  </span>
);

const EmptyState = ({ message }) => (
  <div className={styles.empty_state}>
    <span className={styles.empty_icon}>⊘</span>
    <p>{message}</p>
  </div>
);

const parseWidth = (w) => {
  if (!w) return 150;
  const n = parseInt(w, 10);
  return isNaN(n) ? 150 : n;
};

export const DataTableV2 = ({
  columns = [],
  data = [],
  onRowClick = null,
  onSort = null,
  rowSelect = false,
  onRowSelect = null,
  emptyMessage = "No records to display",
  loading = false,
  rowKey = "id",
  filterSlot,
  ...rest
}) => {
  const { sortBy = { column: null, direction: null } } = rest;
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [colWidths, setColWidths] = useState(() => Object.fromEntries(columns.map((col) => [col.accessor, parseWidth(col.width)])));
  const resizeRef = useRef(null);
  const [resizingCol, setResizingCol] = useState(null);

  const wrapperRef = useRef(null);
  const userResizedCols = useRef(new Set());

  const fixedBodyRef = useRef(null);
  const scrollBodyRef = useRef(null);
  const scrollHeadRef = useRef(null);

  const fixedColumns = columns.filter((col) => col.fixed);
  const scrollColumns = columns.filter((col) => !col.fixed);
  const hasFixedColumns = fixedColumns.length > 0;

  useEffect(() => {
    setColWidths((prev) => {
      const next = { ...prev };
      columns.forEach((col) => {
        if (!(col.accessor in next)) next[col.accessor] = parseWidth(col.width);
      });
      return next;
    });
  }, [columns]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizeRef.current) return;
      const { accessor, startX, startWidth } = resizeRef.current;
      const minWidth = 60;
      const delta = e.clientX - startX;
      const newWidth = Math.max(minWidth, startWidth + delta);
      setColWidths((prev) => ({ ...prev, [accessor]: newWidth }));
    };

    const onMouseUp = () => {
      if (!resizeRef.current) return;
      userResizedCols.current.add(resizeRef.current.accessor);
      resizeRef.current = null;
      setResizingCol(null);
      document.body.classList.remove(styles.resizing_cursor);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const handleResizeStart = useCallback(
    (e, accessor) => {
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = {
        accessor,
        startX: e.clientX,
        startWidth: colWidths[accessor] ?? 150,
      };
      setResizingCol(accessor);
      document.body.classList.add(styles.resizing_cursor);
    },
    [colWidths]
  );

  // ─── Scroll Sync ──────────────────────────────────────────────────────────
  useEffect(() => {
    const scrollBody = scrollBodyRef.current;
    const fixedBody = fixedBodyRef.current;
    const scrollHead = scrollHeadRef.current;
    if (!scrollBody) return;

    const handleScroll = () => {
      if (fixedBody) fixedBody.scrollTop = scrollBody.scrollTop;
      if (scrollHead) scrollHead.scrollLeft = scrollBody.scrollLeft;
    };

    scrollBody.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollBody.removeEventListener("scroll", handleScroll);
  }, [data]);

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const recalc = (containerWidth) => {
      const checkboxW = rowSelect ? 40 : 0;
      const available = containerWidth - checkboxW;
      const fixedW = columns.reduce((sum, col) => {
        const isLocked = col.width != null || userResizedCols.current.has(col.accessor);
        return isLocked ? sum + (colWidths[col.accessor] ?? parseWidth(col.width)) : sum;
      }, 0);
      const autoCols = columns.filter((col) => col.width == null && !userResizedCols.current.has(col.accessor));
      if (!autoCols.length) return;
      const autoW = Math.max(80, Math.floor((available - fixedW) / autoCols.length));
      setColWidths((prev) => {
        const next = { ...prev };
        autoCols.forEach((col) => {
          next[col.accessor] = autoW;
        });
        return next;
      });
    };
    const ro = new ResizeObserver((entries) => recalc(entries[0].contentRect.width));
    ro.observe(el);
    recalc(el.getBoundingClientRect().width);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, rowSelect]);

  // ─── Sort ───────────────────────────────────────────────────────────────────
  const handleSort = useCallback(
    (accessor) => {
      if (!onSort) return;
      const next =
        sortBy.column !== accessor
          ? { column: accessor, direction: "asc" }
          : sortBy.direction === "asc"
          ? { column: accessor, direction: "desc" }
          : { column: accessor, direction: "asc" };
      onSort(next.column, next.direction);
    },
    [onSort, sortBy]
  );

  const getRowKey = useCallback((row, i) => row[rowKey] ?? i, [rowKey]);

  const handleSelectAll = useCallback(() => {
    const allSelectableKeys = new Set(
      data.reduce((acc, row, i) => {
        if (!row.isDisabled) acc.push(getRowKey(row, i));
        return acc;
      }, [])
    );

    const isAllSelected = [...allSelectableKeys].every((k) => selectedIds.has(k));
    const next = isAllSelected ? new Set() : allSelectableKeys;

    setSelectedIds(next);
    onRowSelect?.(isAllSelected ? [] : data.filter((row, i) => allSelectableKeys.has(getRowKey(row, i))));
  }, [data, onRowSelect, getRowKey, selectedIds]);

  const handleSelectRow = useCallback(
    (row, i, e) => {
      e.stopPropagation();
      const key = getRowKey(row, i);
      const next = new Set(selectedIds);
      next.has(key) ? next.delete(key) : next.add(key);

      setSelectedIds(next);
      onRowSelect?.(data.filter((r, idx) => next.has(getRowKey(r, idx))));
    },
    [getRowKey, selectedIds, onRowSelect, data]
  );

  const selectableKeys = data.filter((row) => !row.isDisabled).map((row, i) => getRowKey(row, i));

  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selectedIds.has(k));
  const someSelected = selectableKeys.some((k) => selectedIds.has(k));

  const getCellValue = (row, col) => {
    const value = row[col.accessor];

    if (col.Cell) {
      return col.Cell({ value, row });
    }

    if (col.type === "image" && value) {
      return (
        <div className={styles.image_wrapper}>
          <img
            src={value}
            alt={col.Header}
            className={styles.table_image}
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.nextSibling.style.display = "block";
            }}
            loading="lazy"
          />

          <div className={styles.image_placeholder} />
        </div>
      );
    }

    return value ?? "—";
  };

  const getColStyle = (col) => {
    const w = `${colWidths[col.accessor] ?? parseWidth(col.width)}px`;
    return { width: w, minWidth: w, maxWidth: w };
  };

  const renderHeaderCell = (col, idx) => (
    <div
      key={col.accessor || idx}
      className={`${styles.th} ${onSort && col.sortable ? styles.th_sortable : ""}`}
      style={getColStyle(col)}
      onClick={() => col.sortable && handleSort(col.accessor)}
      role={col.sortable ? "button" : undefined}
      aria-sort={sortBy.column === col.accessor ? sortBy.direction : undefined}
    >
      <span className={styles.th_label}>{col.Header}</span>
      {onSort && col.sortable && <SortIcon direction={sortBy.column === col.accessor ? sortBy.direction : null} />}
      <div
        className={`${styles.resize_handle} ${resizingCol === col.accessor ? styles.resize_active : ""}`}
        onMouseDown={(e) => handleResizeStart(e, col.accessor)}
        onClick={(e) => e.stopPropagation()}
        title="Drag to resize column"
      />
    </div>
  );

  const renderBodyCell = (row, col, idx) => (
    <div
      key={col.accessor || idx}
      className={styles.td}
      style={getColStyle(col)}
      title={typeof row[col.accessor] === "string" ? row[col.accessor] : undefined}
    >
      <span className={styles.td_content}>{getCellValue(row, col)}</span>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.wrapper} ref={wrapperRef}>
        <div className={styles.skeleton_header} />
        {[...Array(5)].map((_, i) => (
          <div key={i} className={styles.skeleton_row} style={{ animationDelay: `${i * 0.07}s` }} />
        ))}
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className={styles.wrapper} ref={wrapperRef}>
        <div className={`${styles.thead_row} ${styles.thead_empty}`}>
          {rowSelect && <div className={`${styles.th} ${styles.th_checkbox}`} />}
          {columns.map(renderHeaderCell)}
        </div>
        <EmptyState message={emptyMessage} />
      </div>
    );
  }

  if (hasFixedColumns) {
    return (
      <div className={styles.wrapper} ref={wrapperRef}>
        <div className={styles.split_layout}>
          {/* Fixed Side */}
          <div className={styles.fixed_side}>
            <div className={`${styles.thead_row} ${styles.fixed_thead}`}>
              {rowSelect && (
                <div className={`${styles.th} ${styles.th_checkbox}`}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={handleSelectAll}
                    aria-label="Select all rows"
                  />
                </div>
              )}
              {fixedColumns.map(renderHeaderCell)}
            </div>
            <div className={styles.fixed_body} ref={fixedBodyRef}>
              {data.map((row, rowIdx) => (
                <div
                  key={rowIdx}
                  className={`${styles.tr} ${onRowClick ? styles.tr_clickable : ""} ${
                    selectedIds.has(getRowKey(row, rowIdx)) ? styles.tr_selected : ""
                  } ${row.isDisabled ? styles.tr_strike : ""}`}
                  onClick={() => onRowClick?.(row)}
                  data-testid={`selectedRow${rowIdx}`}
                >
                  {rowSelect && (
                    <div className={`${styles.td} ${styles.td_checkbox}`}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={selectedIds.has(getRowKey(row, rowIdx))}
                        disabled={row.isDisabled}
                        onChange={(e) => handleSelectRow(row, rowIdx, e)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select row ${rowIdx + 1}`}
                      />
                    </div>
                  )}
                  {fixedColumns.map((col, colIdx) => renderBodyCell(row, col, colIdx))}
                </div>
              ))}
            </div>
          </div>

          <div className={styles.scroll_side}>
            <div className={styles.scroll_head_wrapper} ref={scrollHeadRef}>
              <div className={styles.thead_row}>{scrollColumns.map(renderHeaderCell)}</div>
            </div>
            <div className={styles.scroll_body} ref={scrollBodyRef}>
              {data.map((row, rowIdx) => (
                <div
                  key={rowIdx}
                  className={`${styles.tr} ${onRowClick ? styles.tr_clickable : ""} ${
                    selectedIds.has(getRowKey(row, rowIdx)) ? styles.tr_selected : ""
                  } ${row.isDisabled ? styles.tr_strike : ""}`}
                  onClick={() => onRowClick?.(row)}
                  data-testid={`selectedRow${rowIdx}`}
                >
                  {scrollColumns.map((col, colIdx) => renderBodyCell(row, col, colIdx))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      {filterSlot && <div className={styles.filter_slot}>{filterSlot}</div>}
      <div className={styles.table_scroll}>
        <div className={styles.thead_row}>
          {rowSelect && (
            <div className={`${styles.th} ${styles.th_checkbox}`}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={handleSelectAll}
                aria-label="Select all rows"
              />
            </div>
          )}
          {columns.map(renderHeaderCell)}
        </div>
        {data.map((row, rowIdx) => (
          <div
            key={rowIdx}
            className={`${styles.tr} ${onRowClick ? styles.tr_clickable : ""} ${selectedIds.has(getRowKey(row, rowIdx)) ? styles.tr_selected : ""} ${
              row.isDisabled ? styles.tr_strike : ""
            }`}
            onClick={() => onRowClick?.(row)}
            data-testid={`selectedRow${rowIdx}`}
          >
            {rowSelect && (
              <div className={`${styles.td} ${styles.td_checkbox}`}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={selectedIds.has(getRowKey(row, rowIdx))}
                  disabled={row.isDisabled}
                  onChange={(e) => handleSelectRow(row, rowIdx, e)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select row ${rowIdx + 1}`}
                />
              </div>
            )}
            {columns.map((col, colIdx) => renderBodyCell(row, col, colIdx))}
          </div>
        ))}
      </div>
    </div>
  );
};

DataTableV2.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      fixed: PropTypes.bool,
      sortable: PropTypes.bool,
      width: PropTypes.string,
      minWidth: PropTypes.string,
      Cell: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRowClick: PropTypes.func,
  onSort: PropTypes.func,
  rowSelect: PropTypes.bool,
  onRowSelect: PropTypes.func,
  emptyMessage: PropTypes.string,
  loading: PropTypes.bool,
  rowKey: PropTypes.string,
};

export default DataTableV2;
